import { Calendar } from '@creditornot/cb-icons';
import { Tag } from '@creditornot/cb-tag';
import React, { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { WeeklyTimeSpan } from 'apis/restaurant-api/merchant-admin/menu2/types';
import {
  fromWeeklyScheduleInputEntriesToWeeklySchedule,
  fromWeeklyScheduleToTimePairsByWeekday,
  fromWeeklyTimeSpansToWeeklyScheduleInputEntries,
} from 'common/components/weekly-schedules';
import { Typography as T } from 'common/typography';
import time from 'common/utils/time';
import { WEEKDAYS } from 'common/utils/time/values';

type Props = {
  className?: string;
  weeklySchedule: WeeklyTimeSpan[] | null;
};

const weekdays: { [key in Weekday]: string } = {
  monday: 'week.mon',
  tuesday: 'week.tue',
  wednesday: 'week.wed',
  thursday: 'week.thu',
  friday: 'week.fri',
  saturday: 'week.sat',
  sunday: 'week.sun',
};

const Root = styled.div`
  flex: 1 1 auto;
  max-width: 250px;
  display: flex;
  flex-direction: column;
`;

const ScheduleTable = styled.section`
  display: table;

  tr {
    padding-block-end: 12px;
  }

  tr > td {
    &:last-child {
      > * {
        padding-block-end: 12px;
      }
    }
  }
`;

const TagContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const StyledTag = styled(Tag).attrs({ variant: 'lightBlack' })`
  display: flex;
  align-items: baseline;
  align-self: flex-start;
`;

const StyledCalendar = styled(Calendar).attrs({ width: 12, height: 12 })`
  margin-inline-end: 4px;
`;

const EmptyContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-grow: 0;
`;

export const NewWeeklyScheduleView = ({ className, weeklySchedule }: Props) => {
  const timePairsForWeekdays = useMemo(() => {
    if (weeklySchedule === null) {
      return null;
    }
    const weeklyScheduleInputEntries = fromWeeklyTimeSpansToWeeklyScheduleInputEntries(weeklySchedule);
    const schedule = fromWeeklyScheduleInputEntriesToWeeklySchedule(weeklyScheduleInputEntries);

    return fromWeeklyScheduleToTimePairsByWeekday(schedule);
  }, [weeklySchedule]);

  if (timePairsForWeekdays === null) {
    return <FormattedMessage id="time-of-week.always" />;
  }

  if (WEEKDAYS.every(day => timePairsForWeekdays[day].length === 0)) {
    return <FormattedMessage id="time-of-week.never" />;
  }

  return (
    <Root className={className}>
      <ScheduleTable>
        {WEEKDAYS.map(day => (
          <tr>
            <td>
              <T.Body3>
                <FormattedMessage id={weekdays[day]} />
              </T.Body3>
            </td>
            <td>
              {timePairsForWeekdays[day].length > 0 ? (
                <TagContainer>
                  {timePairsForWeekdays[day].map((pair, index) => (
                    <StyledTag key={index}>
                      <StyledCalendar />
                      {time.renderMinutesFromMidnight(pair.start || 0, false)}
                      {' - '}
                      {time.renderMinutesFromMidnight(pair.end || 0, true)}
                    </StyledTag>
                  ))}
                </TagContainer>
              ) : (
                <EmptyContainer>-</EmptyContainer>
              )}
            </td>
          </tr>
        ))}
      </ScheduleTable>
    </Root>
  );
};
